import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { galleryConfig } from '../config/galleryConfig'
import { useToast } from '@/components/ui/ToastContainer'
import type { GalleryAlbum, GalleryImage } from '../types'

const PAGE_SIZE = 1000

interface GalleryContextType {
  albums: GalleryAlbum[]
  images: GalleryImage[]
  selectedAlbumId: string | null
  isLoading: boolean
  error: string | null
  // Pagination
  currentPage: number
  totalPages: number
  totalImageCount: number
  goToPage: (page: number) => void
  // Likes functionality
  likedImageIds: Set<string>
  showLikedOnly: boolean
  // Purchase functionality
  purchasedImageIds: Set<string>
  showPurchasedOnly: boolean

  fetchAlbums: () => Promise<void>
  fetchAllImages: () => Promise<void>
  fetchAlbumImages: (albumId: string) => Promise<void>
  refreshImageUrl: (imageId: string, resolution: 'medium' | 'full' | 'original') => Promise<string>
  setSelectedAlbumId: (id: string | null) => void
  // Likes functionality
  toggleLike: (imageId: string) => Promise<void>
  setShowLikedOnly: (show: boolean) => void
  // Purchase functionality
  setShowPurchasedOnly: (show: boolean) => void
  markImageAsPurchased: (imageId: string) => void
}

const GalleryContext = createContext<GalleryContextType | undefined>(undefined)

export function GalleryProvider({ children }: { children: React.ReactNode }) {
  const [albums, setAlbums] = useState<GalleryAlbum[]>([])
  const [images, setImages] = useState<GalleryImage[]>([])
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Pagination
  const [currentPage, setCurrentPage] = useState(0)
  const [totalImageCount, setTotalImageCount] = useState(0)
  // Likes state
  const [likedImageIds, setLikedImageIds] = useState<Set<string>>(new Set())
  const [showLikedOnly, setShowLikedOnly] = useState(false)
  // Purchase state
  const [purchasedImageIds, setPurchasedImageIds] = useState<Set<string>>(new Set())
  const [showPurchasedOnly, setShowPurchasedOnly] = useState(false)

  const totalPages = Math.max(1, Math.ceil(totalImageCount / PAGE_SIZE))

  // Track current fetch context to prevent stale responses
  const fetchContextRef = useRef(0)
  // Track in-flight like toggles to prevent rapid double-clicks
  const toggleInFlightRef = useRef<Set<string>>(new Set())
  const { showError } = useToast()

  const getAuthHeader = (): Record<string, string> => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      return { 'Authorization': `Bearer ${token}` }
    }
    return {}
  }

  // Fetch all albums
  const fetchAlbums = useCallback(async () => {
    try {
      setError(null)

      const response = await fetch(`${galleryConfig.api.baseURL}/api/gallery/albums`, {
        headers: getAuthHeader()
      })

      if (!response.ok) throw new Error('Failed to fetch albums')

      const result = await response.json()
      setAlbums(result.data || [])
    } catch (err: any) {
      console.error('Fetch albums error:', err)
      setError(err.message)
    }
  }, [])

  // Fetch a page of images (used by both "All" and album views)
  const fetchImagesPage = useCallback(async (page: number, albumId?: string | null) => {
    try {
      const ctx = ++fetchContextRef.current
      setIsLoading(true)
      setError(null)

      const offset = page * PAGE_SIZE
      let url = `${galleryConfig.api.baseURL}/api/gallery/images?limit=${PAGE_SIZE}&offset=${offset}`
      if (albumId) {
        url += `&album_id=${albumId}`
      }

      const response = await fetch(url, { headers: getAuthHeader() })
      if (!response.ok) throw new Error('Failed to fetch images')
      if (ctx !== fetchContextRef.current) return // stale

      const result = await response.json()
      setImages(result.data || [])

      // Update total count from response if available, otherwise from albums
      if (result.pagination?.total !== undefined) {
        setTotalImageCount(result.pagination.total)
      }
    } catch (err: any) {
      console.error('Fetch images error:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Fetch ALL images (default view) — first page
  const fetchAllImages = useCallback(async () => {
    setCurrentPage(0)
    await fetchImagesPage(0, null)
  }, [fetchImagesPage])

  // Fetch images for a specific album — first page
  const fetchAlbumImages = useCallback(async (albumId: string) => {
    setCurrentPage(0)

    // Find album to get its slug
    const album = albums.find(a => a.id === albumId)
    if (!album) {
      await fetchImagesPage(0, albumId)
    } else {
      // Use slug for cleaner API
      try {
        const ctx = ++fetchContextRef.current
        setIsLoading(true)
        setError(null)

        const response = await fetch(`${galleryConfig.api.baseURL}/api/gallery/albums/${album.slug}`, {
          headers: getAuthHeader()
        })

        if (!response.ok) throw new Error('Failed to fetch album')
        if (ctx !== fetchContextRef.current) return

        const result = await response.json()
        const data = result.images || []
        setImages(data)
        setTotalImageCount(data.length)
      } catch (err: any) {
        console.error('Fetch album images error:', err)
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }
  }, [albums, fetchImagesPage])

  // Navigate to a specific page
  const goToPage = useCallback((page: number) => {
    const clamped = Math.max(0, Math.min(page, totalPages - 1))
    if (clamped === currentPage) return
    setCurrentPage(clamped)
    fetchImagesPage(clamped, selectedAlbumId)
  }, [currentPage, totalPages, selectedAlbumId, fetchImagesPage])

  // Refresh an expired signed URL
  const refreshImageUrl = useCallback(async (imageId: string, resolution: 'medium' | 'full' | 'original'): Promise<string> => {
    try {
      const response = await fetch(
        `${galleryConfig.api.baseURL}/api/gallery/images/${imageId}/url?resolution=${resolution}`,
        { headers: getAuthHeader() }
      )

      if (!response.ok) throw new Error('Failed to refresh URL')

      const result = await response.json()

      // Update the image in state with new URL
      setImages(prev => prev.map(img => {
        if (img.id === imageId) {
          return {
            ...img,
            urls: {
              ...img.urls,
              [resolution]: result.url
            }
          }
        }
        return img
      }))

      return result.url
    } catch (err: any) {
      console.error('Refresh URL error:', err)
      throw err
    }
  }, [])

  // Fetch user's liked images with full data (for Liked tab view)
  const fetchLikedImages = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const token = localStorage.getItem('auth_token')
      if (!token) {
        setImages([])
        return
      }

      const response = await fetch(`${galleryConfig.api.baseURL}/api/gallery/likes`, {
        headers: getAuthHeader()
      })

      if (!response.ok) throw new Error('Failed to fetch liked images')

      const result = await response.json()
      const likedImages = result.data || []
      setImages(likedImages)
      setTotalImageCount(likedImages.length)

      setLikedImageIds(new Set(likedImages.map((img: GalleryImage) => img.id)))
    } catch (err: any) {
      console.error('Fetch liked images error:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Fetch user's liked image IDs only (lightweight endpoint for initial mount)
  const fetchLikedImageIds = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const response = await fetch(`${galleryConfig.api.baseURL}/api/gallery/likes/ids`, {
        headers: getAuthHeader()
      })

      if (!response.ok) {
        console.warn('Failed to fetch liked image IDs')
        return
      }

      const result = await response.json()
      const ids: string[] = result.data || []
      setLikedImageIds(new Set(ids))
    } catch (err: any) {
      console.warn('Fetch liked image IDs error:', err)
    }
  }, [])

  // Toggle like on an image (optimistic update with race guard and server reconciliation)
  const toggleLike = useCallback(async (imageId: string) => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      showError('Please log in to like images')
      return
    }

    // Prevent concurrent API calls for the same image (rapid double-clicks)
    if (toggleInFlightRef.current.has(imageId)) {
      return
    }
    toggleInFlightRef.current.add(imageId)

    const wasLiked = likedImageIds.has(imageId)

    // Optimistic update
    setLikedImageIds(prev => {
      const next = new Set(prev)
      if (wasLiked) {
        next.delete(imageId)
      } else {
        next.add(imageId)
      }
      return next
    })

    try {
      const response = await fetch(`${galleryConfig.api.baseURL}/api/gallery/images/${imageId}/like`, {
        method: 'POST',
        headers: getAuthHeader()
      })

      if (!response.ok) {
        // Revert optimistic update
        setLikedImageIds(prev => {
          const next = new Set(prev)
          if (wasLiked) {
            next.add(imageId)
          } else {
            next.delete(imageId)
          }
          return next
        })

        const errorBody = await response.json().catch(() => ({}))
        if (errorBody.code === 'PROFILE_NOT_FOUND') {
          showError('Unable to save like. Please try logging out and back in.')
        } else {
          showError('Failed to save like. Please try again.')
        }
        return
      }

      // Reconcile with server state to ensure consistency
      const result = await response.json()
      if (typeof result.liked === 'boolean') {
        setLikedImageIds(prev => {
          const next = new Set(prev)
          if (result.liked) {
            next.add(imageId)
          } else {
            next.delete(imageId)
          }
          return next
        })
      }
    } catch (err: any) {
      // Revert optimistic update on network error
      setLikedImageIds(prev => {
        const next = new Set(prev)
        if (wasLiked) {
          next.add(imageId)
        } else {
          next.delete(imageId)
        }
        return next
      })
      console.error('Toggle like error:', err)
      showError('Network error. Please check your connection.')
    } finally {
      toggleInFlightRef.current.delete(imageId)
    }
  }, [likedImageIds, showError])

  // Fetch user's purchased image IDs
  const fetchPurchasedImageIds = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const response = await fetch(`${galleryConfig.api.baseURL}/api/gallery/purchases/ids`, {
        headers: getAuthHeader()
      })

      if (!response.ok) {
        console.warn('Failed to fetch purchased images')
        return
      }

      const result = await response.json()
      setPurchasedImageIds(new Set(result.data || []))
    } catch (err: any) {
      console.warn('Fetch purchased images error:', err)
    }
  }, [])

  // Mark an image as purchased
  const markImageAsPurchased = useCallback((imageId: string) => {
    setPurchasedImageIds(prev => {
      const next = new Set(prev)
      next.add(imageId)
      return next
    })

    setImages(prev => prev.map(img => {
      if (img.id === imageId) {
        return { ...img, hasAccess: true }
      }
      return img
    }))
  }, [])

  // Compute total image count from albums for "All" view
  useEffect(() => {
    if (selectedAlbumId === null && !showLikedOnly) {
      const total = albums.reduce((sum, a) => sum + a.image_count, 0)
      if (total > 0) setTotalImageCount(total)
    } else if (selectedAlbumId) {
      const album = albums.find(a => a.id === selectedAlbumId)
      if (album) setTotalImageCount(album.image_count)
    }
  }, [albums, selectedAlbumId, showLikedOnly])

  // Load albums, liked images, and purchased images on mount
  useEffect(() => {
    fetchAlbums()
    fetchLikedImageIds()
    fetchPurchasedImageIds()
  }, [fetchAlbums, fetchLikedImageIds, fetchPurchasedImageIds])

  // Load images when album selection, liked filter, or purchased filter changes
  useEffect(() => {
    if (showLikedOnly) {
      fetchLikedImages()
    } else if (showPurchasedOnly) {
      // When showing purchased only, we need all images loaded so we can filter
      fetchAllImages()
    } else if (selectedAlbumId === null) {
      fetchAllImages()
    } else {
      fetchAlbumImages(selectedAlbumId)
    }
  }, [selectedAlbumId, showLikedOnly, showPurchasedOnly, fetchAllImages, fetchAlbumImages, fetchLikedImages])

  const value: GalleryContextType = {
    albums,
    images,
    selectedAlbumId,
    isLoading,
    error,
    currentPage,
    totalPages,
    totalImageCount,
    goToPage,
    likedImageIds,
    showLikedOnly,
    purchasedImageIds,
    showPurchasedOnly,
    fetchAlbums,
    fetchAllImages,
    fetchAlbumImages,
    refreshImageUrl,
    setSelectedAlbumId,
    toggleLike,
    setShowLikedOnly,
    setShowPurchasedOnly,
    markImageAsPurchased
  }

  return (
    <GalleryContext.Provider value={value}>
      {children}
    </GalleryContext.Provider>
  )
}

export function useGallery() {
  const context = useContext(GalleryContext)
  if (!context) {
    throw new Error('useGallery must be used within GalleryProvider')
  }
  return context
}
